import { useQuery } from '@tanstack/react-query';
import { Building2, Info, Loader2, Printer, TrendingUp, Users, Wrench } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { ScrollX } from './ScrollX';

const WINDOWS: Array<[number, string]> = [
  [6, '6 months'],
  [12, '12 months'],
  [24, '24 months'],
];

const kes = (value: number | null): string =>
  value === null ? '—' : `KES ${value.toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;

/** `2026-08` → `Aug 26`, which is what fits under a bar. */
const shortMonth = (month: string): string => {
  const [year, m] = month.split('-');
  const date = new Date(Number(year), Number(m) - 1, 1);
  return `${date.toLocaleString('en-KE', { month: 'short' })} ${String(year).slice(2)}`;
};

/**
 * The business over months, for somebody outside it.
 *
 * Built for the conversation with a lender. A bank asks four questions — is
 * there a real customer base, is it growing, does the business deliver what it
 * promises, and does it know where its money goes — and every figure here
 * answers one of them from records written for another purpose. That is what
 * makes them worth anything: nobody assembled this to look good on an
 * application.
 *
 * **It says plainly that it is not revenue.** The app records jobs and costs
 * and issues no invoices, so presenting these as income would be the one thing
 * that destroys the document's credibility in the room. The disclosure is at
 * the top rather than in a footnote, because a lender who works that out for
 * themselves stops trusting the rest of it.
 */
export function BusinessReport() {
  const [months, setMonths] = useState(12);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['business-report', months],
    queryFn: () => api.businessReport(months),
  });

  if (isPending) {
    return (
      <div className="card flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card">
        <p className="text-sm text-danger-700">{(error as Error).message}</p>
      </div>
    );
  }

  const { customers, delivery, costs, capacity, monthly } = data;
  const busiest = Math.max(1, ...monthly.map((m) => m.opened));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-xl">The business</h3>
          <p className="text-sm text-muted-foreground">
            Twelve months of trading, drawn from the day-to-day records.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-xl bg-muted p-1">
            {WINDOWS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMonths(value)}
                aria-pressed={months === value}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  months === value
                    ? 'bg-card text-primary-700 shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* Print is how this actually reaches a bank — as paper, or a PDF
              made from it. Nothing here depends on colour to be readable. */}
          <button type="button" onClick={() => window.print()} className="btn-secondary">
            <Printer className="h-4 w-4" aria-hidden="true" />
            Print
          </button>
        </div>
      </div>

      {/*
        Said first and said plainly. A lender who discovers this for themselves
        stops believing the rest of the document.
      */}
      {!data.revenueTracked && (
        <p className="flex items-start gap-2 rounded-2xl border border-accent-200 bg-accent-50 p-4 text-sm text-accent-700">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <strong>These are operations, not income.</strong> {data.basis}
          </span>
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          {
            icon: Users,
            label: 'Customers served',
            figure: String(customers.servedInPeriod),
            hint: `${customers.active} active on the books`,
          },
          {
            icon: Wrench,
            label: 'Jobs completed',
            figure: String(delivery.jobsClosed),
            hint: `${delivery.completionRate ?? '—'}% finished first time`,
          },
          {
            icon: TrendingUp,
            label: 'Promises kept',
            figure: delivery.slaCompliance === null ? '—' : `${delivery.slaCompliance}%`,
            hint: `${delivery.breached} missed their deadline`,
          },
          {
            icon: Building2,
            label: 'Cost per job',
            figure: kes(costs.perClosedJobKes),
            hint: `${kes(costs.totalKes)} in approved claims`,
          },
        ].map((tile) => (
          <div key={tile.label} className="stat-tile">
            <tile.icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
            <p className="stat-figure">{tile.figure}</p>
            <p className="text-sm font-medium">{tile.label}</p>
            <p className="stat-label text-xs">{tile.hint}</p>
          </div>
        ))}
      </div>

      {/*
        The trend, which a lender reads before any single figure. Bars are
        labelled with their own numbers rather than relying on the axis, so the
        page survives being printed in black and white.
      */}
      <div className="card">
        <h4 className="font-heading text-lg">Work each month</h4>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Jobs opened, and how many of them were finished.
        </p>

        <ScrollX className="mt-4">
          <div className="flex min-w-[34rem] items-end gap-2">
            {monthly.map((row) => (
              <div key={row.month} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-xs font-semibold tabular-nums">{row.opened}</span>
                <div
                  className="flex w-full flex-col justify-end rounded-t-lg bg-primary-100"
                  style={{ height: `${Math.max(4, (row.opened / busiest) * 120)}px` }}
                  role="img"
                  aria-label={`${shortMonth(row.month)}: ${row.opened} opened, ${row.closed} closed`}
                >
                  <div
                    className="w-full rounded-t-lg bg-primary-500"
                    style={{
                      height: `${row.opened ? (row.closed / row.opened) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-[10px] text-muted-foreground">{shortMonth(row.month)}</span>
              </div>
            ))}
          </div>
        </ScrollX>

        <p className="mt-3 text-xs text-muted-foreground">
          The solid part of each bar is the work that was finished. Figures above each bar are jobs
          opened that month.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card">
          <h4 className="font-heading text-lg">Where the money goes</h4>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Approved claims only. A pending claim is a request, not a cost.
          </p>
          <table className="mt-3 w-full text-sm">
            <tbody className="divide-y divide-border">
              {costs.byCategory.map((row) => (
                <tr key={row.category}>
                  <td className="py-2 capitalize">{row.category}</td>
                  <td className="py-2 text-right font-semibold tabular-nums">
                    {kes(row.amountKes)}
                  </td>
                </tr>
              ))}
              {costs.byCategory.length === 0 && (
                <tr>
                  <td className="py-4 text-center text-muted-foreground" colSpan={2}>
                    No approved claims in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="card">
          <h4 className="font-heading text-lg">What the business runs on</h4>
          <dl className="mt-3 grid grid-cols-2 gap-3">
            {[
              ['Technicians', String(capacity.technicians)],
              ['Jobs per technician', capacity.jobsPerTechnician === null ? '—' : String(capacity.jobsPerTechnician)],
              ['Areas covered', String(capacity.areasCovered)],
              ['Network equipment', String(capacity.networkElements)],
              ['Business customers', String(customers.business)],
              ['Home customers', String(customers.home)],
              [
                'Typical repair time',
                delivery.medianResolutionHours === null
                  ? '—'
                  : `${delivery.medianResolutionHours} h`,
              ],
              [
                'Customer rating',
                delivery.satisfaction === null
                  ? 'Not yet rated'
                  : `${delivery.satisfaction} of 5 (${delivery.ratingCount})`,
              ],
            ].map(([label, figure]) => (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="font-semibold tabular-nums">{figure}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Generated {new Date(data.generatedAt).toLocaleString('en-KE')} from GoSpeedNet Dispatch.
        Every figure is derived from operational records — job timestamps, approved expense claims
        and customer ratings — rather than entered for this report.
      </p>
    </div>
  );
}
